import {useState, useCallback, useMemo} from 'react'
import {useQuery, useMutation, useQueryClient, type QueryClient} from '@tanstack/react-query'
import {Link} from 'react-router-dom'
import {Check, Eraser, Loader2, RotateCcw} from 'lucide-react'
import {ApiError} from '@/api/client'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {useToast} from '@/hooks/use-toast'
import {routes} from '@/lib/routes'
import {Button} from '@/components/ui/button'
import {Combobox, type ComboboxOption} from '@/components/ui/combobox'
import {Skeleton} from '@/components/ui/skeleton'
import {TagInput} from '@/components/ui/tag-input'
import {ErrorBanner} from '@/components/ui/error-banner'
import type {AttributeDefinition} from '@/types/attribute-definition'
import type {AttributeValue} from '@/types/attribute'
import {AttributeValueType} from '@/types/attribute'
import {AttributeOperation} from '@/types/attribute-operation'
import {PartyResponseFields} from '@/types/party'
import {AttributeValueInput} from './AttributeValueInput'
import {AttributeHint} from './AttributeHint'
import type {AttributeEntityType} from '@/types/attribute-entity-type'
import {validateValue, getHintText} from '@/lib/attribute-validation'
import {AttributeAuthorityBadge, type AuthorityEntityType} from '@/components/AttributeAuthorityBadge'
import type {Source} from '@/types/source'

interface InlineAttributeEditorProps {
    entityType: AttributeEntityType
    entityId: string
    entityTypeCode?: string
    canEdit: boolean
    /**
     * Sources currently linked to this entity (Spec §6.2 — multi-source-sync). Used
     * by the {@link AttributeAuthorityBadge} Transfer-Authority dialog to populate
     * the eligible TargetSystem picker. Empty / undefined renders the badges as
     * read-only (no Transfer option).
     */
    sources?: Source[]
}

/** Entity types that currently expose authority release / transfer REST endpoints (Spec §4.4). */
const AUTHORITY_API_ENTITY_TYPES = new Set<AttributeEntityType>([
    'identities',
    'parties',
])

interface AttributeRow {
    definition: AttributeDefinition
    attribute: AttributeValue | null
}

interface RowState {
    value: string
    multiValues: string[]
    dirty: boolean
    saving: boolean
    error: string | null
    pendingValue: string
    pendingError: string | null
}

function isMultiValued(definition: AttributeDefinition): boolean {
    const max = definition.validationRules?.maxOccurrences
    return max != null && max > 1
}

/**
 * Invalidate caches rooted at the entityType plural noun (`['parties', ...]`
 * for a Party edit, `['identities', ...]` for an Identity edit, …) on
 * attribute mutations. Triggers active observers (other open list pages,
 * detail pages) to refetch immediately — without this, an active list view
 * would not see the change until its next mount.
 */
function invalidateEntityCaches(client: QueryClient, entityType: string, entityId: string): void {
    client.invalidateQueries({queryKey: [entityType]})
    client.invalidateQueries({queryKey: [entityType, entityId, PartyResponseFields.attributes]})
}

function extractValue(attr: AttributeValue | null): string {
    if (!attr || attr.value === null || attr.value === undefined) return ''
    return String(attr.value)
}

function extractMultiValues(attr: AttributeValue | null): string[] {
    if (!attr?.values) return []
    return attr.values.map(String)
}

// UUID pattern for detecting reference values
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * Renders a REFERENCE attribute value as a resolved party link instead of a raw UUID.
 */
function ReferenceDisplay({value}: {value: string}) {
    const services = useServices()
    const isUuid = UUID_RE.test(value)
    const {data: party} = useQuery({
        queryKey: ['party-reference', value],
        queryFn: () => services.parties.detail(value),
        enabled: isUuid,
        retry: false,
    })
    if (!isUuid) return <span className="text-sm">{value}</span>
    if (party) {
        return (
            <Link to={routes.identityManagement.parties.detail(value)} className="text-sm text-primary hover:underline" data-testid="reference-link">
                {party.derivedDisplayName}
            </Link>
        )
    }
    return <span className="text-sm text-muted-foreground">{value}</span>
}

/**
 * Editable REFERENCE attribute rendered as a party search Combobox.
 */
function ReferenceCombobox({value, onChange, disabled, testId}: {
    value: string
    onChange: (value: string) => void
    disabled?: boolean
    testId?: string
}) {
    const i18n = useI18n()
    const [searchQuery, setSearchQuery] = useState('')

    const isUuid = UUID_RE.test(value)
    const {data: selectedParty} = useQuery({
        queryKey: ['party-reference', value],
        queryFn: () => services.parties.detail(value),
        enabled: !!value && isUuid,
        retry: false,
    })

    const services = useServices()
    // Search parties
    const {data: searchResults, isLoading} = useQuery({
        queryKey: ['party-search-attr', searchQuery],
        queryFn: () => services.parties.list({q: searchQuery, size: 10}),
        enabled: searchQuery.length >= 2,
    })

    const options: ComboboxOption[] = (searchResults?.content ?? []).map((p) => ({
        value: String(p.id),
        label: p.derivedDisplayName,
    }))

    return (
        <Combobox
            options={options}
            value={value || null}
            onValueChange={(val) => onChange(val ?? '')}
            onSearch={setSearchQuery}
            placeholder={i18n.inlineAttributeEditor.searchParty}
            isLoading={isLoading}
            selectedLabel={selectedParty?.derivedDisplayName}
            data-testid={testId}
            disabled={disabled}
        />
    )
}

function isApplicable(def: AttributeDefinition, entityTypeCode?: string): boolean {
    if (!def.typeCodes || def.typeCodes.length === 0) return true
    if (!entityTypeCode) return true
    return def.typeCodes.includes(entityTypeCode)
}

function canPerformOperation(attr: AttributeValue | null, def: AttributeDefinition, operation: string): boolean {
    if (!attr) {
        // No saved value — check the definition's operations instead
        return def.operations.includes(operation)
    }
    return attr.allowedOperations.includes(operation)
}

/* eslint-disable i18next/no-literal-string -- data-testid values, CSS classes, API paths */
export function InlineAttributeEditor({
    entityType, entityId, entityTypeCode, canEdit, sources}: InlineAttributeEditorProps) {
    const services = useServices()
    const i18n = useI18n()
    const {toast} = useToast()
    const queryClient = useQueryClient()

    const [rowStates, setRowStates] = useState<Record<string, RowState>>({})

    const definitionsQuery = useQuery({
        queryKey: ['attribute-definitions', entityType],
        queryFn: () => services.attributeDefinitions.list(entityType),
    })

    const attributesQuery = useQuery({
        queryKey: [entityType, entityId, PartyResponseFields.attributes],
        queryFn: () => services.entityAttributes.list(entityType, entityId) as Promise<AttributeValue[]>,
        enabled: !!entityId,
    })

    const applicableDefinitions = useMemo(() =>
            (definitionsQuery.data ?? []).filter((d: import('@/types/attribute-definition').AttributeDefinition) => d.active && isApplicable(d, entityTypeCode)),
        [definitionsQuery.data, entityTypeCode]
    )

    const rows: AttributeRow[] = useMemo(() => {
        const attrs = attributesQuery.data ?? []
        return applicableDefinitions.map(def => {
            const attr = attrs.find(a => a.definitionId === def.id) ?? null
            return {definition: def, attribute: attr}
        })
    }, [applicableDefinitions, attributesQuery.data])

    const requiredRows = useMemo(() =>
            rows.filter(r => r.definition.required).sort((a, b) => a.definition.sortOrder - b.definition.sortOrder),
        [rows]
    )

    const optionalRows = useMemo(() =>
            rows.filter(r => !r.definition.required).sort((a, b) => a.definition.sortOrder - b.definition.sortOrder),
        [rows]
    )

    const getRowState = useCallback((def: AttributeDefinition, attr: AttributeValue | null): RowState => {
        const existing = rowStates[def.id]
        const base: RowState = {
            value: isMultiValued(def) ? '' : extractValue(attr),
            multiValues: isMultiValued(def) ? extractMultiValues(attr) : [],
            dirty: false,
            saving: false,
            error: null,
            pendingValue: '',
            pendingError: null,
        }
        if (!existing) return base
        return {...base, ...existing}
    }, [rowStates])

    const updateRowState = useCallback((defId: string, update: Partial<RowState>) => {
        setRowStates(prev => ({
            ...prev,
            [defId]: {...(prev[defId] ?? {} as RowState), ...update},
        }))
    }, [])

    const handleValueChange = useCallback((def: AttributeDefinition, attr: AttributeValue | null, newValue: string) => {
        const originalValue = extractValue(attr)
        updateRowState(def.id, {
            value: newValue,
            dirty: newValue !== originalValue,
            error: null,
        })
    }, [updateRowState])

    const handleBlur = useCallback((def: AttributeDefinition) => {
        const state = rowStates[def.id]
        if (!state || isMultiValued(def)) return
        const error = validateValue(def, state.value, i18n)
        if (error) {
            updateRowState(def.id, {error})
        }
    }, [rowStates, i18n, updateRowState])

    const handleReset = useCallback((def: AttributeDefinition, attr: AttributeValue | null) => {
        if (isMultiValued(def)) {
            updateRowState(def.id, {
                multiValues: extractMultiValues(attr),
                dirty: false,
                error: null,
                pendingValue: '',
                pendingError: null,
            })
        } else {
            updateRowState(def.id, {
                value: extractValue(attr),
                dirty: false,
                error: null,
            })
        }
    }, [updateRowState])

    const saveMutation = useMutation({
        mutationFn: async ({def, attr, state}: {def: AttributeDefinition; attr: AttributeValue | null; state: RowState}) => {
            const isNew = !attr
            const multi = isMultiValued(def)
            const isEmpty = multi ? state.multiValues.length === 0 : state.value === ''

            // Delete the existing record when user cleared the value
            if (isEmpty && !isNew) {
                return services.entityAttributes.remove(entityType, entityId, attr!.id)
            }
            // Nothing to do if clearing a value that was never saved
            if (isEmpty) return

            if (multi) {
                const vt = def.valueType
                const typedValues = state.multiValues.map(v => AttributeValueType.toBody(vt, v))
                const body: Record<string, unknown> = {
                    attributeName: def.name,
                    values: typedValues,
                }
                if (isNew) {
                    return services.entityAttributes.create(entityType, entityId, body)
                }
                return services.entityAttributes.update(entityType, entityId, body)
            }

            const vt = def.valueType
            const body: Record<string, unknown> = {
                attributeName: def.name,
                [AttributeValueType.bodyField(vt)]: AttributeValueType.toBody(vt, state.value),
            }
            if (isNew) {
                return services.entityAttributes.create(entityType, entityId, body)
            }
            return services.entityAttributes.update(entityType, entityId, body)
        },
        onSuccess: (_data, {def, state, attr}) => {
            const multi = isMultiValued(def)
            const isEmpty = multi ? state.multiValues.length === 0 : state.value === ''
            const wasExisting = !!attr
            const msg = isEmpty && wasExisting ? i18n.inlineAttributeEditor.removeSuccess : i18n.inlineAttributeEditor.saveSuccess
            toast({description: msg})
            invalidateEntityCaches(queryClient, entityType, entityId)
            setRowStates(prev => {
                const next = {...prev}
                delete next[def.id]
                return next
            })
        },
        onError: (error: Error, {def}) => {
            // Toast is owned by the global mutationCache.onError in main.tsx —
            // never duplicate it here (per frontend.md rule). We only do the
            // mutation-specific UX (state cleanup, cache invalidation, inline
            // field error rendering) that the global handler can't do.
            if (error instanceof ApiError && error.status === 409) {
                invalidateEntityCaches(queryClient, entityType, entityId)
                setRowStates(prev => {
                    const next = {...prev}
                    delete next[def.id]
                    return next
                })
                return
            }
            if (error instanceof ApiError && error.pointer && error.pointer.startsWith('/value')) {
                updateRowState(def.id, {error: error.message, saving: false})
                return
            }
            updateRowState(def.id, {saving: false})
        },
    })

    const handleSave = useCallback((def: AttributeDefinition, attr: AttributeValue | null) => {
        let state = getRowState(def, attr)
        const multi = isMultiValued(def)

        // Auto-commit pending value in TagInput before saving
        if (multi && state.pendingValue.trim()) {
            const trimmed = state.pendingValue.trim()
            const error = validateValue(def, trimmed, i18n)
            if (error) {
                updateRowState(def.id, {pendingError: error})
                return
            }
            if (!state.multiValues.includes(trimmed)) {
                state = {...state, multiValues: [...state.multiValues, trimmed], pendingValue: ''}
            }
        }

        const isEmpty = multi ? state.multiValues.length === 0 : state.value === ''

        // Block save on required attributes with empty value
        if (isEmpty && def.required) {
            updateRowState(def.id, {error: i18n.inlineAttributeEditor.requiredHint})
            return
        }

        if (!isEmpty && !multi) {
            const error = validateValue(def, state.value, i18n)
            if (error) {
                updateRowState(def.id, {error})
                return
            }
        }

        updateRowState(def.id, {saving: true, multiValues: state.multiValues, pendingValue: ''})
        saveMutation.mutate({def, attr, state})
    }, [getRowState, i18n, updateRowState, saveMutation])

    const handleClear = useCallback((def: AttributeDefinition) => {
        if (isMultiValued(def)) {
            updateRowState(def.id, {
                multiValues: [],
                dirty: true,
                error: null,
                pendingValue: '',
                pendingError: null,
            })
        } else {
            updateRowState(def.id, {
                value: '',
                dirty: true,
                error: null,
            })
        }
    }, [updateRowState])

    const handleAddMultiValue = useCallback((def: AttributeDefinition, attr: AttributeValue | null, directValue?: string) => {
        const state = getRowState(def, attr)
        const trimmed = (directValue ?? state.pendingValue).trim()
        if (!trimmed) return

        const error = validateValue(def, trimmed, i18n)
        if (error) {
            updateRowState(def.id, {pendingError: error})
            return
        }

        if (state.multiValues.includes(trimmed)) return

        const originalValues = extractMultiValues(attr)
        const newValues = [...state.multiValues, trimmed]
        updateRowState(def.id, {
            multiValues: newValues,
            pendingValue: '',
            pendingError: null,
            dirty: JSON.stringify(newValues) !== JSON.stringify(originalValues),
        })
    }, [getRowState, i18n, updateRowState])

    const handleRemoveMultiValue = useCallback((def: AttributeDefinition, attr: AttributeValue | null, index: number) => {
        const state = getRowState(def, attr)
        const originalValues = extractMultiValues(attr)
        const newValues = state.multiValues.filter((_, i) => i !== index)
        updateRowState(def.id, {
            multiValues: newValues,
            dirty: JSON.stringify(newValues) !== JSON.stringify(originalValues),
            error: null,
        })
    }, [getRowState, updateRowState])

    // Loading state
    if (definitionsQuery.isLoading || attributesQuery.isLoading) {
        return (
            <div data-testid="inline-attribute-editor" className="space-y-3">
                {[1, 2, 3].map(i => (
                    <div key={i} className="flex items-center gap-4">
                        <Skeleton className="h-4 w-32"/>
                        <Skeleton className="h-9 flex-1"/>
                    </div>
                ))}
            </div>
        )
    }

    // Error state
    if (definitionsQuery.error || attributesQuery.error) {
        return (
            <div data-testid="inline-attribute-editor">
                <ErrorBanner error={definitionsQuery.error ?? attributesQuery.error}/>
                <Button
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    data-testid="inline-attribute-editor-retry"
                    onClick={() => {
                        definitionsQuery.refetch()
                        attributesQuery.refetch()
                    }}
                >
                    {i18n.common.retry}
                </Button>
            </div>
        )
    }

    // Empty state
    if (applicableDefinitions.length === 0) {
        return (
            <div data-testid="inline-attribute-editor" className="text-sm text-muted-foreground py-4">
                {canEdit ? i18n.inlineAttributeEditor.noDefinitionsAdmin : i18n.inlineAttributeEditor.noDefinitions}
            </div>
        )
    }

    function renderRow(row: AttributeRow) {
        const {definition: def, attribute: attr} = row
        const state = getRowState(def, attr)
        const multi = isMultiValued(def)

        const isAuthorityManaged = def.authorityId != null
        const canCreate = !isAuthorityManaged || canPerformOperation(attr, def, AttributeOperation.CREATE)
        const canUpdate = !isAuthorityManaged || canPerformOperation(attr, def, AttributeOperation.UPDATE)
        const canDelete = !isAuthorityManaged || canPerformOperation(attr, def, AttributeOperation.DELETE)

        // Spec §6.2: row-level authority — owned attributes block manual edits until released.
        const isValueAuthorityOwned = attr?.valueAuthorityTargetSystemId != null
        const isEditable = canEdit && (attr ? canUpdate : canCreate) && !isValueAuthorityOwned
        const isRemovable = canEdit && !def.required && attr != null && canDelete && !isValueAuthorityOwned
        const showActionsCol = canEdit
        const supportsAuthorityApi = AUTHORITY_API_ENTITY_TYPES.has(entityType)
        const ownedTooltip = isValueAuthorityOwned
            ? i18n.authority.editDisabledTooltip({systemName: attr?.valueAuthorityTargetSystemName ?? ''})
            : undefined

        return (
            <div
                key={def.id}
                className="flex items-start gap-4 py-2.5"
                data-testid={`attribute-row-${def.name}`}
            >
                {/* Label column */}
                <div className="w-44 shrink-0 pt-2 flex flex-col gap-1">
                    <div className="flex gap-1 items-start">
                        <div className="flex-1 min-w-0">
                            <span className="text-sm font-medium">
                                {def.displayName || def.name}
                                {def.required && <span className="text-destructive ml-0.5">*</span>}
                            </span>
                            {isAuthorityManaged && attr?.authorityName && (
                                <div className="mt-0.5 text-[10px] leading-none text-muted-foreground">
                                    {attr.authorityName}
                                </div>
                            )}
                        </div>
                        <AttributeHint definition={def}/>
                    </div>
                </div>

                {/* Content column */}
                <div className="flex-1 min-w-0" title={ownedTooltip}>
                    {/* REFERENCE: readonly → resolved link, editable → party combobox */}
                    {def.valueType === AttributeValueType.REFERENCE && !isEditable ? (
                        <div className="pt-2">
                            {state.value ? <ReferenceDisplay value={state.value} /> : (
                                <span className="text-sm text-muted-foreground">{'—'}</span>
                            )}
                        </div>
                    ) : def.valueType === AttributeValueType.REFERENCE && isEditable ? (
                        <ReferenceCombobox
                            value={state.value}
                            onChange={(v) => handleValueChange(def, attr, v)}
                            disabled={!isEditable}
                            testId={`attr-${def.name}`}
                        />
                    ) : !multi ? (
                        <AttributeValueInput
                            definition={def}
                            value={state.value}
                            onChange={(v) => handleValueChange(def, attr, v)}
                            onBlur={() => handleBlur(def)}
                            error={state.error}
                            testId={`attr-${def.name}`}
                            showLabel={false}
                            showHint={true}
                            inline={false}
                            readOnly={!isEditable}
                        />
                    ) : (
                        <TagInput
                            values={state.multiValues}
                            onAdd={(val) => handleAddMultiValue(def, attr, val)}
                            onRemove={(idx) => handleRemoveMultiValue(def, attr, idx)}
                            canRemoveItem={() => !(def.required && state.multiValues.length <= 1)}
                            pending={state.pendingValue}
                            onPendingChange={(v) => updateRowState(def.id, {pendingValue: v, pendingError: null})}
                            placeholder={isEditable ? i18n.inlineAttributeEditor.enterToAdd : (def.displayName || def.name)}
                            disabled={!isEditable}
                            error={state.pendingError ?? state.error}
                            hint={isEditable ? getHintText(def, i18n) : null}
                            maxItems={def.validationRules?.maxOccurrences}
                            testId={`attr-${def.name}`}
                            suggestions={def.resolvedAllowedValues?.map(v => ({code: v.code, label: v.label})) ?? undefined}
                        />
                    )}

                    {def.required && !attr && !state.value && !multi && (
                        <p className="text-xs text-muted-foreground mt-1">
                            {i18n.inlineAttributeEditor.requiredHint}
                        </p>
                    )}
                    {def.required && multi && state.multiValues.length === 0 && (
                        <p className="text-xs text-muted-foreground mt-1">
                            {i18n.inlineAttributeEditor.requiredHint}
                        </p>
                    )}
                </div>

                {/* Authority column */}
                <div className="w-44 shrink-0 pt-2 flex items-start">
                    {attr && (
                        <AttributeAuthorityBadge
                            attribute={attr}
                            readOnly={!canEdit || !supportsAuthorityApi}
                            actions={supportsAuthorityApi && canEdit ? {
                                entityType: entityType as AuthorityEntityType,
                                entityId,
                                attributeName: def.name,
                                attributeDisplayName: def.displayName || def.name,
                                sources: sources ?? [],
                                invalidateQueryKeys: [[entityType, entityId, PartyResponseFields.attributes]],
                            } : undefined}
                            data-testid={`attribute-authority-badge-${def.name}`}
                        />
                    )}
                </div>

                {/* Actions column */}
                {showActionsCol && (
                    <div className="w-[4.5rem] shrink-0 flex items-center justify-end gap-1 pt-1">
                        {canEdit && (state.dirty || (multi && state.pendingValue.trim().length > 0)) && (
                            <>
                                <Button
                                    size="icon"
                                    variant="default"
                                    className="h-8 w-8"
                                    onClick={() => handleSave(def, attr)}
                                    disabled={state.saving}
                                    title={i18n.inlineAttributeEditor.save}
                                    data-testid={`attribute-save-${def.name}`}
                                >
                                    {state.saving ? (
                                        <Loader2 className="h-3.5 w-3.5 animate-spin"/>
                                    ) : (
                                        <Check className="h-3.5 w-3.5"/>
                                    )}
                                </Button>
                                <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-8 w-8"
                                    onClick={() => handleReset(def, attr)}
                                    disabled={state.saving}
                                    title={i18n.inlineAttributeEditor.reset}
                                    data-testid={`attribute-reset-${def.name}`}
                                >
                                    <RotateCcw className="h-3.5 w-3.5"/>
                                </Button>
                            </>
                        )}
                        {canEdit && isRemovable && !state.dirty && (
                            <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8 text-muted-foreground hover:text-foreground"
                                onClick={() => handleClear(def)}
                                title={i18n.inlineAttributeEditor.remove}
                                data-testid={`attribute-remove-${def.name}`}
                            >
                                <Eraser className="h-3.5 w-3.5"/>
                            </Button>
                        )}
                    </div>
                )}
            </div>
        )
    }

    return (
        <div data-testid="inline-attribute-editor">
            <div className="divide-y">
                {[...requiredRows, ...optionalRows].map(renderRow)}
            </div>
        </div>
    )
}
/* eslint-enable i18next/no-literal-string */
